import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { authService } from "@/services/authService";
import useToastStore from "@/components/Common/Toast/toastStore";

const VerifyOtpSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  otp: Yup.string()
    .required("OTP is required")
    .length(6, "OTP must be exactly 6 characters"),
});

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email") || "";
  // Handle both null and the string "undefined"
  const rawTempToken = queryParams.get("temp_token");
  const tempToken =
    rawTempToken === "undefined" || !rawTempToken ? "" : rawTempToken;

  console.log("OTP Page Initialized - Email:", email, "Token:", tempToken);
  const { showToast } = useToastStore;

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        email: values.email,
        otp: values.otp,
        temp_token: tempToken,
      };
      console.log("Sending Verify Payload:", payload);
      const response = await authService.verifyOtp(payload);

      // Check user role and navigate accordingly
      const userRole = response?.data?.user?.roles?.[0]?.title?.toLowerCase();
      console.log("User role:", userRole); // Debug log
      if (userRole === "company") {
        const firstCompany = response?.data?.user?.company_logins?.[0];
        navigate(`/dashboard/${firstCompany?.company_slug}`);
      } else if (userRole === "candidate") {
        navigate("/candidate/dashboard");
      } else {
        navigate("/companies");
      }
    } catch (error) {
      if (error.response?.data) {
        const { errors, error: errorObj, message } = error.response.data;
        if (errors || errorObj) {
          const errorMessages = Object.values(errors || errorObj).flat();
          errorMessages.forEach((msg) =>
            showToast({ message: msg, type: "error" }),
          );
        } else if (message) {
          showToast({ message: message, type: "error" });
        }
      } else {
        showToast({
          message: error.message || "Verification failed",
          type: "error",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const otpLength = 6;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-primary/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl w-full space-y-8 bg-card p-10 rounded-xl shadow-lg">
        <div className="-mx-4 flex flex-wrap items-center">
          <div className="w-full px-4 lg:w-1/2">
            <div className="text-center grayscale">
              <img
                src="https://cdn.tailgrids.com/2.0/image/application/images/forms/forms-10.svg"
                alt="illustration"
                className="mx-auto"
              />
            </div>
          </div>
          <div className="w-full px-4 lg:w-1/2">
            <div className="mb-8">
              <h2 className="text-3xl font-extrabold text-foreground">App TRKR</h2>
              <p className="mt-2 text-sm text-foreground">
                Enter the OTP sent to your email
              </p>
            </div>

            <Formik
              initialValues={{
                email: email,
                otp: "",
              }}
              validationSchema={VerifyOtpSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                <Form className="space-y-6">
                  <div className="rounded-md">
                    <div className="mb-6">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-foreground mb-1"
                      >
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={values.email}
                        readOnly
                        className="block w-full px-4 py-3 border border-border bg-muted text-muted-foreground rounded-lg focus:outline-none cursor-not-allowed sm:text-sm transition-all"
                      />
                    </div>
                    <div className="mb-6">
                      <label
                        htmlFor="otp"
                        className="block text-sm font-medium text-foreground mb-1"
                      >
                        OTP Code
                      </label>
                      <input
                        id="otp"
                        name="otp"
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        maxLength={otpLength}
                        value={values.otp}
                        onPaste={(e) => {
                          const paste = e.clipboardData
                            .getData("text")
                            .replace(/[^a-zA-Z0-9]/g, "")
                            .slice(0, otpLength);
                          setFieldValue("otp", paste);
                        }}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/[^a-zA-Z0-9]/g, "")
                            .slice(0, otpLength);
                          setFieldValue("otp", val);
                        }}
                        placeholder="Enter OTP "
                        className="block w-full px-4 py-3 border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg focus:outline-none focus:ring-1 focus:ring-ring sm:text-sm transition-all"
                      />
                      {touched.otp && errors.otp && (
                        <p className="mt-3 text-sm text-red-600">
                          {errors.otp}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-primary-dark hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 active:scale-95"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Verifying...
                        </span>
                      ) : (
                        "Verify OTP"
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
